import React, { FC } from "react";
import classNames from "classnames";
import { flexRender, Table as TTableProps } from "@tanstack/react-table";
import Table, { TBody, Td, TFoot, Th, THead, Tr } from "../../components/ui/Table";
import Icon from "../../components/icon/Icon";
import { CardFooter, CardFooterChild } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/form/Input";
import Select from "../../components/form/Select";

export const TableHeaderTemplate = ({ table }) => {
  return (
    <THead>
      {table.getHeaderGroups().map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <Th
              key={header.id}
              isColumnBorder={false}
              className={classNames({
                "text-left": header.id !== "Actions",
                "text-right": header.id === "Actions",
              })}
            >
              {header.isPlaceholder ? null : (
                <div
                  key={header.id}
                  aria-hidden="true"
                  {...{
                    className: header.column.getCanSort() ? "cursor-pointer select-none flex items-center" : "",
                    onClick: header.column.getToggleSortingHandler(),
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                    desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                  }[header.column.getIsSorted()] ?? null}
                </div>
              )}
            </Th>
          ))}
        </Tr>
      ))}
    </THead>
  );
};

export const TableBodyTemplate = ({ table }) => {
  return (
    <TBody>
      {table.getRowModel().rows.map((row) => (
        <Tr key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <Td
              key={cell.id}
              className={classNames({
                "text-left": cell.column.id !== "Actions",
                "text-right": cell.column.id === "Actions",
              })}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Td>
          ))}
        </Tr>
      ))}
    </TBody>
  );
};

export const TableFooterTemplate = ({ table }) => {
  return (
    <TFoot>
      {table.getFooterGroups().map((footerGroup) => (
        <Tr key={footerGroup.id}>
          {footerGroup.headers.map((header) => (
            <Th
              key={header.id}
              isColumnBorder={false}
              className={classNames({
                "text-left": header.id !== "Actions",
                "text-right": header.id === "Actions",
              })}
            >
              {header.isPlaceholder ? null : (
                <div
                  key={header.id}
                  aria-hidden="true"
                  {...{
                    className: header.column.getCanSort() ? "cursor-pointer select-none flex items-center" : "",
                    onClick: header.column.getToggleSortingHandler(),
                  }}
                >
                  {flexRender(header.column.columnDef.footer, header.getContext())}
                  {{
                    asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                    desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                  }[header.column.getIsSorted()] ?? null}
                </div>
              )}
            </Th>
          ))}
        </Tr>
      ))}
    </TFoot>
  );
};

export const TableCardFooterTemplate = ({ pagination, setPageSize, setPageIndex }) => {
  return (
    <CardFooter>
      <CardFooterChild>
        <Select
          value={pagination?.showRecords}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
          className="!w-fit"
          name="pageSize"
        >
          {[5, 10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize} className="bg-white dark:bg-zinc-950">
              Show {pageSize}
            </option>
          ))}
        </Select>
      </CardFooterChild>
      <CardFooterChild>
        <Button
          onClick={() => setPageIndex(1)}
          isDisable={pagination.currectPage <= 1}
          icon="HeroChevronDoubleLeft"
          className="!px-0"
        />
        <Button
          onClick={() => setPageIndex(pagination?.currectPage - 1)}
          isDisable={pagination.currectPage <= 1}
          icon="HeroChevronLeft"
          className="!px-0"
        />
        <span className="flex items-center gap-1">
          <div>Page</div>
          <strong>
            <Input value={pagination?.currectPage} className="inline-flex !w-12 text-center" name="page" /> of{" "}
            {pagination?.pageCount}
          </strong>
        </span>
        <Button
          onClick={() => setPageIndex(pagination?.currectPage + 1)}
          isDisable={pagination.currectPage >= pagination?.pageCount}
          icon="HeroChevronRight"
          className="!px-0"
        />
        <Button
          onClick={() => setPageIndex(pagination?.pageCount)}
          isDisable={pagination.currectPage >= pagination?.pageCount}
          icon="HeroChevronDoubleRight"
          className="!px-0"
        />
      </CardFooterChild>
    </CardFooter>
  );
};

const TableTemplate = (props) => {
  const { children, hasHeader = true, hasFooter = true, table, ...rest } = props;

  return (
    <Table {...rest}>
      {children || (
        <>
          {hasHeader && <TableHeaderTemplate table={table} />}
          <TableBodyTemplate table={table} />
          {hasFooter && <TableFooterTemplate table={table} />}
        </>
      )}
    </Table>
  );
};

export default TableTemplate;
